/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util (pure) — in-app feedback (FEEDBACK-PLAN, slice fb-private): the CSS selectors of the
 *              personal data a feedback screenshot must never carry (patient / customer name, CF, contacts,
 *              clinical text, amounts, calendar event text on a clinic). The capture covers every match with a
 *              solid box before the PNG leaves the browser (Sentry data region is US).
 *   extends:   -
 *   template:  none
 *   exports:   PRIVATE_SELECTORS (frozen string[], always covered, [data-nv-private] first),
 *              CLINIC_SELECTORS (frozen string[], added on a clinic),
 *              privateSelectors({ isClinic }) (a fresh string[]: the two lists, clinic ones only on a clinic)
 *   uses:      -
 *   siblings:  nv-feedback-capture.js (the only caller: covers the matches), nv-feedback-describe.js,
 *              nv-feedback-trail.js, nv-feedback-send.js
 *   tests:     medical tests/unit/lib/nv-feedback-private (unit)
 *   gotchas:   the markers live in the «UI nuova» templates only (classic DOM untouched):
 *              - `data-nv-private` on an element = the element is covered (prefer a whole block over its cells);
 *              - `data-nv-private-head='title sub'` on a page root = the title and / or subtitle of the shared
 *                header inside it (NvPageHeader / NvDetailHeader / NvFormHead) is covered — those blocks are
 *                shared components a page cannot put an attribute on.
 *              dhtmlx builds the calendar events itself (no attribute possible): their text is selected by
 *              the dhtmlx classes, on a clinic only. `.hide-for-privacy` / `.nv-privacy` are the top-bar eye's
 *              own markers (event names, money): on a clinic they are covered too.
 *              Every entry must stay a valid selector on its own: the capture may query them one by one, and
 *              one invalid selector in a joined list would throw for the whole list.
 */

// Always covered, whatever the tenant: explicit template markers + what the user typed where it is private.
const PRIVATE_SELECTORS = Object.freeze(['[data-nv-private]', "[data-nv-private-head~='title'] :is(.nv-ph-title, .nv-dh-title)", "[data-nv-private-head~='sub'] :is(.nv-ph-sub, .nv-fhead-sub)",
// the top-bar search term is usually a person's name
'.nv-tb-search-input', "input[type='password']"]);

// A clinic adds: the calendar event text (dhtmlx markup), the eye's privacy markers and every free-text / typed
// value (clinical sheets, patient forms, notes editors).
const CLINIC_SELECTORS = Object.freeze(['.dhx_cal_event .dhx_title', '.dhx_cal_event .dhx_body', '.dhx_cal_event_line', '.dhx_cal_event_clear', '.dhtmlXTooltip', '.hide-for-privacy', '.nv-privacy', 'textarea', '.ql-editor', "[contenteditable='true']", "input:not([type='checkbox'], [type='radio'], [type='range'], [type='color'], [type='button'], [type='submit'], [type='reset'], [type='hidden'], [type='file'])"]);

/**
 * The selectors a feedback screenshot covers on this tenant.
 * @param {{ isClinic?: boolean }} [options]
 * @returns {string[]} a fresh array (callers may extend it): PRIVATE_SELECTORS, then CLINIC_SELECTORS on a clinic
 */
function privateSelectors({
  isClinic
} = {}) {
  return isClinic ? [...PRIVATE_SELECTORS, ...CLINIC_SELECTORS] : [...PRIVATE_SELECTORS];
}

export { CLINIC_SELECTORS, PRIVATE_SELECTORS, privateSelectors };
//# sourceMappingURL=nv-feedback-private.js.map
